import { useEffect, useState } from 'react';
import {
  GetMonitorArrivals,
  MonitorArrival,
  MonitorPickup,
} from '@/apis/monitor';
import useUnauthorize from '@/hooks/useUnauthorize';
import { startPolling } from './polling';

export const ARRIVALS_REFRESH_MS = 60 * 1000;
export const ARRIVALS_WINDOW_HOURS = 4;

export interface MonitorBoard {
  /** Null until the first answer for this screen. */
  arrivals: MonitorArrival[] | null;
  /** Due to be picked up soon; null until the first answer. */
  pickups: MonitorPickup[] | null;
}

/**
 * Polls the arrivals board endpoint (arrivals and pickups). A failed refresh
 * keeps the last good rows on screen rather than blanking the board.
 */
export const useMonitorBoard = (
  token: string | null | undefined,
  teamIds?: string[]
): MonitorBoard => {
  const teamKey = teamIds?.join(',') ?? '';
  const { unauthorize } = useUnauthorize();
  const [board, setBoard] = useState<MonitorBoard>({
    arrivals: null,
    pickups: null,
  });

  useEffect(() => {
    // A new team (or session) must not show the previous team's rows while
    // it loads; a failed interval refresh still keeps the last good rows.
    setBoard({ arrivals: null, pickups: null });
    if (!token) return undefined;
    // One request at a time, cancelled when the screen moves on or when it
    // hangs; a cancelled or failed refresh keeps the last good rows.
    return startPolling(
      (signal) =>
        GetMonitorArrivals(
          token,
          ARRIVALS_WINDOW_HOURS,
          teamKey ? teamKey.split(',') : undefined,
          signal
        )
          .then((res) => {
            const body = res?.data?.data;
            if (!signal.aborted && body?.is_success) {
              setBoard({
                arrivals: body.data?.arrivals ?? [],
                pickups: body.data?.pickups ?? [],
              });
            }
          })
          .catch((error) => {
            if (!signal.aborted) unauthorize(error);
          }),
      { intervalMs: ARRIVALS_REFRESH_MS }
    );
  }, [token, teamKey, unauthorize]);

  return board;
};

/** The arrivals alone (see useMonitorBoard). */
export const useMonitorArrivals = (
  token: string | null | undefined,
  teamIds?: string[]
): MonitorArrival[] | null => useMonitorBoard(token, teamIds).arrivals;
